import { useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Mail, Building2 } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "");

// Per-ROE "Email employee" / "Notify employer" — enabled only once the Service Canada filing is confirmed.
export function ROENotifyButtons({ det, onDone }) {
  const [busy, setBusy] = useState("");
  const filed = det.filing_status === "FILED_CONFIRMED";
  const hint = !filed ? "Confirm the Service Canada filing first" : "";
  const send = async (who) => {
    setBusy(who);
    try {
      const { data } = await api.post(`/roe-workflow/${det.roe_id}/notify/${who}`);
      toast.success(who === "employee" ? `ROE email sent to ${data.to}` : `Employer notified (${data.to.join(", ")})`);
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const btn = "inline-flex items-center gap-1.5 border border-black px-3 py-2 text-sm hover:bg-black hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-inherit";
  return (
    <>
      <button data-testid="roe-email-employee" onClick={() => send("employee")} disabled={!filed || !!busy || !det.employee_email}
        title={hint || (!det.employee_email ? "Employee has no email on file" : det.notified_employee_at ? `Sent ${fmt(det.notified_employee_at)} — click to resend` : "Email the employee: ROE submitted, view in portal")} className={btn}>
        <Mail className="h-4 w-4" />{det.notified_employee_at ? `Emailed ${fmt(det.notified_employee_at)}` : "Email employee"}
      </button>
      <button data-testid="roe-notify-employer" onClick={() => send("employer")} disabled={!filed || !!busy}
        title={hint || (det.notified_employer_at ? `Sent ${fmt(det.notified_employer_at)} — click to resend` : "Email the client admins: ROE submitted to Service Canada")} className={btn}>
        <Building2 className="h-4 w-4" />{det.notified_employer_at ? `Employer notified ${fmt(det.notified_employer_at)}` : "Notify employer"}
      </button>
    </>
  );
}

// T4 year-end: "Email employees" (all slips not yet emailed) / "Notify employer" — enabled once CRA filing is confirmed.
export function T4NotifyButtons({ companyId, year, ws, onDone }) {
  const [busy, setBusy] = useState("");
  const filed = ws.filing_status === "FILED_CONFIRMED";
  const hint = !filed ? "Confirm the CRA filing first" : "";
  const done = Object.keys(ws.notified_employees || {}).length;
  const total = (ws.slips || []).length;
  const send = async (who) => {
    setBusy(who);
    try {
      const body = who === "employees" ? { employee_ids: done >= total && total > 0 ? (ws.slips || []).map((s) => s.employee_id) : null } : undefined;
      const { data } = await api.post(`/t4-yearend/${companyId}/${year}/notify/${who}`, body);
      toast.success(who === "employees" ? `T4 email sent to ${data.sent} employee(s)${data.skipped?.length ? ` · ${data.skipped.length} skipped (no email)` : ""}` : `Employer notified (${data.to.join(", ")})`);
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const btn = "inline-flex items-center gap-1.5 border border-black px-3 py-2 text-sm hover:bg-black hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-inherit";
  return (
    <>
      <button data-testid="t4-email-employees" onClick={() => send("employees")} disabled={!filed || !!busy || total === 0}
        title={hint || (done >= total ? "All employees emailed — click to resend to everyone" : `Email the ${total - done} employee(s) not yet notified`)} className={btn}>
        <Mail className="h-4 w-4" />{done > 0 ? `Emailed ${done}/${total} employees` : "Email employees"}
      </button>
      <button data-testid="t4-notify-employer" onClick={() => send("employer")} disabled={!filed || !!busy}
        title={hint || (ws.notified_employer_at ? `Sent ${fmt(ws.notified_employer_at)} — click to resend` : "Email the client admins: T4s filed with CRA")} className={btn}>
        <Building2 className="h-4 w-4" />{ws.notified_employer_at ? `Employer notified ${fmt(ws.notified_employer_at)}` : "Notify employer"}
      </button>
    </>
  );
}

export function ROENotifyEmployerAll({ companyId, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/roe-workflow/${companyId}/notify/employer-all`);
      toast.success(`Employer notified of ${data.roes} ROE(s)`); onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <button data-testid="roe-notify-employer-all" onClick={run} disabled={busy} title="One email listing every filed ROE not yet announced to the employer"
      className="inline-flex items-center gap-1.5 border border-black px-3 py-1.5 text-xs hover:bg-black hover:text-white disabled:opacity-50">
      <Building2 className="h-3.5 w-3.5" /> Notify employer (all filed)
    </button>
  );
}
